/**
 * "Select Baseline" — choose which baseline (and which of its configs) is drawn on the charts.
 *
 * A baseline holds a separate fit per boat CONFIG, so the picker is two selects: the baseline, then
 * the configuration within it. Showing every config at once would put several white lines over a
 * chart that already carries a target line and a fit line.
 */
import { For, Show, createEffect, createMemo, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';
import ColorPicker from '../utilities/ColorPicker';
import { PERFORMANCE_TARGET_COLOR_OPTIONS } from '../../utils/performanceTargetColor';
import { isAdministrator } from '../../utils/userPermissions';
import { user } from '../../store/userStore';

export const NO_BASELINE_OPTION = 'No baseline';

export interface BaselineDisplayOption {
  baseline_id: number;
  name: string;
}

export interface BaselineDisplayModalProps {
  show: boolean;
  onClose: () => void;
  /** Heading, e.g. "Select Baseline 2". Defaults to "Select Baseline". */
  title?: string;
  baselines: BaselineDisplayOption[];
  /** `NO_BASELINE_OPTION` or the stringified baseline id. */
  selectedKey: string;
  onSelectedKeyChange: (value: string) => void;
  /** Configs available in the currently selected baseline. */
  configs: string[];
  selectedConfig: string;
  onSelectedConfigChange: (value: string) => void;
  baselineColor: string;
  onBaselineColorChange: (color: string) => void;
  loading: boolean;
  /** Which action is in flight, so only that button shows a spinner label. */
  loadingAction: 'select' | 'remove' | null;
  error: string | null;
  onConfirm: () => void;
  onRemove: () => void;
  /**
   * Download the baselines as .xlsx. Optional: only the pages that have chart channels and an
   * x-axis to export against pass it, and the button is hidden everywhere else.
   */
  onExport?: () => void;
  /** True while a download is being built, so the button can say so. */
  exporting?: boolean;
}

/** A real baseline is selected, as opposed to the "No baseline" sentinel. */
function isBaselineKey(key: string): boolean {
  const id = Number(key);
  return Number.isInteger(id) && id > 0;
}

export default function BaselineDisplayModal(props: BaselineDisplayModalProps) {
  const [showColorPicker, setShowColorPicker] = createSignal(false);

  /**
   * Keep each select's DOM value pinned to the state it is showing.
   *
   * `value` alone is not enough: the modal is opened first and its option lists arrive after the
   * fetch, so the browser has nothing matching to select and falls back to the first option. The
   * picker then read "No baseline" while a baseline was drawn, and choosing "No baseline" fired no
   * change event — so Select re-applied the old baseline instead of clearing it.
   *
   * The element is held in a signal rather than a plain ref so the effect re-runs when it attaches,
   * and the option arrays are read so it re-runs again once they load.
   */
  const [baselineSelect, setBaselineSelect] = createSignal<HTMLSelectElement>();
  const [configSelect, setConfigSelect] = createSignal<HTMLSelectElement>();

  createEffect(() => {
    const el = baselineSelect();
    const key = props.selectedKey;
    void props.baselines.length;
    if (el && el.isConnected && el.value !== key) { el.value = key; }
  });

  createEffect(() => {
    const el = configSelect();
    const config = props.selectedConfig;
    void props.configs.length;
    if (el && el.isConnected && el.value !== config) { el.value = config; }
  });

  const selectEnabled = createMemo(() => !props.loading && Boolean(props.selectedKey));
  const removeEnabled = createMemo(() => !props.loading && isBaselineKey(props.selectedKey));
  const selectLabel = createMemo(() =>
    props.loading && props.loadingAction === 'select' ? 'Selecting…' : 'Select');
  const removeLabel = createMemo(() =>
    props.loading && props.loadingAction === 'remove' ? 'Removing…' : 'Remove');

  /**
   * The export writes the picker's current selection, so it needs a real baseline chosen and is
   * held while a download is being built — a second click would produce a duplicate file.
   */
  const exportEnabled = createMemo(() =>
    !props.exporting && !props.loading && isBaselineKey(props.selectedKey) && Boolean(props.selectedConfig));
  const exportLabel = createMemo(() => (props.exporting ? 'Exporting…' : 'Download Excel'));

  const handleClose = () => {
    setShowColorPicker(false);
    props.onClose();
  };

  return (
    <Show when={props.show}>
      <Portal
        mount={typeof document !== 'undefined'
          ? (document.getElementById('main-content') || document.body)
          : undefined}
      >
        <div class="pagesettings-overlay" onClick={handleClose}>
          <div
            class="pagesettings-modal target_modal baseline-display-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', 'z-index': 10001 }}
          >
            <div
              class="flex justify-between items-center p-4 border-b"
              style="border-color: var(--color-border-primary);"
            >
              <h2 class="text-lg font-semibold" style="color: var(--color-text-primary);">
                {props.title || 'Select Baseline'}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                class="text-gray-500 hover:text-gray-700 transition-colors"
                style="color: var(--color-text-secondary);"
                aria-label="Close"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="p-6">
              <Show when={props.error}>
                <p class="baseline-modal-error" role="alert">{props.error}</p>
              </Show>

              <select
                ref={setBaselineSelect}
                class="target-modal-select"
                value={props.selectedKey}
                onChange={(e) => props.onSelectedKeyChange(e.currentTarget.value)}
              >
                <option value={NO_BASELINE_OPTION}>{NO_BASELINE_OPTION}</option>
                <For each={props.baselines}>
                  {(baseline) => (
                    <option value={String(baseline.baseline_id)}>{baseline.name}</option>
                  )}
                </For>
              </select>

              <Show when={isBaselineKey(props.selectedKey)}>
                <Show
                  when={props.configs.length > 0}
                  fallback={
                    <p class="baseline-modal-hint">
                      This baseline has no fitted configurations yet.
                    </p>
                  }
                >
                  <label class="baseline-modal-label" style="color: var(--color-text-secondary);">
                    Configuration
                  </label>
                  <select
                    ref={setConfigSelect}
                    class="target-modal-select"
                    value={props.selectedConfig}
                    onChange={(e) => props.onSelectedConfigChange(e.currentTarget.value)}
                  >
                    <For each={props.configs}>
                      {(config) => <option value={config}>{config}</option>}
                    </For>
                  </select>
                </Show>
              </Show>

              <div class="target-modal-footer baseline-display-modal-footer">
                <div class="target-modal-color-control">
                  <span class="target-modal-color-label">Baseline color</span>
                  <button
                    type="button"
                    class="target-modal-color-swatch"
                    style={{ 'background-color': props.baselineColor }}
                    aria-label="Choose baseline color"
                    onClick={() => setShowColorPicker((open) => !open)}
                  />
                  <Show when={showColorPicker()}>
                    <div class="target-modal-color-picker-popover">
                      <ColorPicker
                        colors={[...PERFORMANCE_TARGET_COLOR_OPTIONS]}
                        onSelect={(color) => {
                          props.onBaselineColorChange(color);
                          setShowColorPicker(false);
                        }}
                      />
                    </div>
                  </Show>
                </div>

                <div class="target-modal-actions">
                  {/* Upwind and downwind numbers for the chart's channels, on the chart's x-axis. */}
                  <Show when={props.onExport}>
                    <button
                      type="button"
                      onClick={() => props.onExport?.()}
                      disabled={!exportEnabled()}
                      classList={{ 'is-enabled': exportEnabled() }}
                      class="baseline-display-modal-export-btn px-4 py-2 text-sm rounded-md transition-colors"
                    >
                      {exportLabel()}
                    </button>
                  </Show>
                  {/* Deleting a shared reference is admin-only; the server checks write access too. */}
                  <Show when={isAdministrator(user())}>
                    <button
                      type="button"
                      onClick={props.onRemove}
                      disabled={!removeEnabled()}
                      classList={{ 'is-enabled': removeEnabled() }}
                      class="baseline-display-modal-remove-btn px-4 py-2 text-sm rounded-md transition-colors"
                    >
                      {removeLabel()}
                    </button>
                  </Show>
                  <button
                    type="button"
                    onClick={handleClose}
                    class="px-4 py-2 text-sm rounded-md transition-colors"
                    style="background-color: var(--color-bg-button-secondary); color: var(--color-text-button-secondary);"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={props.onConfirm}
                    disabled={!selectEnabled()}
                    class="px-4 py-2 text-sm rounded-md transition-colors font-medium"
                    style={`background-color: ${selectEnabled() ? '#16a34a' : 'var(--color-bg-button)'}; color: var(--color-text-inverse); ${selectEnabled() ? '' : 'cursor: not-allowed; opacity: 0.6;'}`}
                  >
                    {selectLabel()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
